import { useFlag } from '@listening/frontend/analytics/client';
import { voicesAtom } from '@listening/frontend/query/voices';
import { createFileRoute, linkOptions } from '@tanstack/react-router';
import { cacheHeadersFor, experimentalRoutes } from '@/analytics';
import { MARKETING_CACHE_CONTROL } from '@/analytics/cache-headers';
import { Homepage25Body } from '@/components/astro/new-design-25/homepage-25-body';
import type { FunnelStaticStep } from '@/components/funnel/config/funnels';
import * as m from '@/paraglide/messages';
import { hreflangLinks, seo } from '@/seo/seo';

const ANALYTICS = experimentalRoutes['/_marketing/'];

export const Route = createFileRoute('/_marketing/')({
  // `cdn-shared` with the `homepage-cta-funnel-v1` variant in the cache key.
  // The homepage was already on the shared-cache allow-list
  // (`cached-routes.ts`), so nothing per-user renders here; this registration
  // adds the experiment dimension. The explicit MARKETING_CACHE_CONTROL keeps
  // the layout's pre-experiment policy (s-maxage=3600 + stale-if-error)
  // instead of `cacheHeadersFor`'s shorter default.
  staticData: { analytics: ANALYTICS },
  headers: cacheHeadersFor(ANALYTICS, MARKETING_CACHE_CONTROL),
  // TryListening / RowOneFeatures both render voice lists, so the HTML carries
  // them and `voicesAtom` travels with the page. A failed read doesn't reject
  // `ensure`: every consumer falls back to the built-in catalog
  // (`voiceCatalogAtom`).
  loader: async ({ context: { atoms } }) => {
    await atoms.ensure(voicesAtom);
  },
  component: HomepageRoute,
  head: () => ({
    meta: [
      ...seo({
        title: m.meta_homepage_title(),
        description: m.meta_homepage_description(),
      }),
    ],
    links: [...hreflangLinks('/')],
  }),
});

/**
 * `homepage-cta-funnel-v1`: treatment routes the homepage's signup CTAs into
 * the onboarding funnel instead of `/app/continue`. Only the destination
 * changes — the buttons, their labels, and their `cta_clicked` analytics stay
 * identical.
 *
 * ALL of them move together, not just the hero: nav bar, hero, SaveTime, the
 * demo banner, both app sliders, the testimonials button, the FAQ button and
 * the closing banner are one conversion path rendered in several places. If
 * only the hero moved, a visitor who scrolled past it and clicked the nav
 * would sit in the treatment denominator without ever meeting the treatment.
 * `Homepage25Body` fans the destination out through `CtaButtonContext`.
 *
 * The flag is read HERE, not inside `Homepage25Body`: that component is also
 * the control arm of `academic-landing-funnel-v1` on `/academic`, and the
 * PostHog override that `applyEdgeFlags` persists for an enrolled homepage
 * visitor would leak this flag's treatment into that page's CTAs if the
 * shared component read it.
 */
// Both arms declared in one place so the experiment site reads as "which
// destination". `control: undefined` is deliberate, not a shortcut: it means
// "no override", so no provider mounts and every CTA resolves the shared
// default (`DEFAULT_CTA_LINK` → `/app/continue`, locale-prefixed by
// `useCtaHref`) — the same thing every non-experimental page renders. The
// treatment destination is compile-checked against the route tree (route
// path, and the funnel step name via `satisfies`).
const CTA_ARMS = {
  control: undefined,
  treatment: linkOptions({
    to: '/onboarding/$step',
    params: { step: 'welcome' satisfies FunnelStaticStep },
  }),
} as const;

function HomepageRoute() {
  const { value } = useFlag('homepageCtaFunnelV1');
  return <Homepage25Body ctaLink={CTA_ARMS[value]} />;
}
